import { EventEmitter } from 'events';
import { SpriteData, FrameData } from './data.js';

function createTypedEventEmitter() {
    return new EventEmitter();
}
class ProtoSpriteDataMap {
    data;
    frameMap = new Map();
    layerMap = new Map();
    layerNameMap = new Map();
    animationMap = new Map();
    reverseAnimationMap = new Map();
    layerGroupSet = new Set();
    layerGroupsDown = new Map();
    layerGroupsUp = new Map();
    constructor(data) {
        if (data !== undefined) {
            this.data = data;
            this.remap();
        }
        else {
            this.data = new SpriteData();
        }
    }
    remap() {
        this.frameMap.clear();
        this.layerMap.clear();
        this.layerNameMap.clear();
        this.animationMap.clear();
        this.reverseAnimationMap.clear();
        for (const frame of this.data.frames) {
            this.frameMap.set(frame.index, frame);
        }
        for (const layer of this.data.layers) {
            this.layerMap.set(layer.index, layer);
            this.layerNameMap.set(layer.name, layer);
        }
        for (const animation of this.data.animations) {
            this.animationMap.set(animation.name, animation);
            this.reverseAnimationMap.set(animation.indexStart, animation);
        }
        this.layerGroupSet.clear();
        this.layerGroupsDown.clear();
        this.layerGroupsUp.clear();
        for (const layer of this.data.layers) {
            if (layer.isGroup)
                this.layerGroupSet.add(layer.index);
            if (layer.parentIndex) {
                const parentLayer = this.layerMap.get(layer.parentIndex);
                if (parentLayer !== undefined) {
                    let downGroup = this.layerGroupsDown.get(parentLayer.index);
                    if (downGroup === undefined) {
                        downGroup = [];
                        this.layerGroupsDown.set(parentLayer.index, downGroup);
                    }
                    downGroup.push(layer);
                    this.layerGroupsUp.set(layer.index, parentLayer);
                }
            }
        }
    }
}
class ProtoSpriteInstanceAnimationState {
    events = createTypedEventEmitter();
    currentFrame = 0;
    currentFrameDurationRemaining = 0;
    currentAnimation;
    speed = 1;
    loop = true;
    dataMap;
    constructor(dataMap) {
        this.dataMap = dataMap;
        if (dataMap.data.frames.length > 0) {
            const firstFrame = dataMap.data.frames.at(this.currentFrame);
            if (firstFrame === undefined)
                throw new Error("No frames available.");
            this.currentFrameDurationRemaining = firstFrame.duration;
        }
    }
    startAnimation(animationName) {
        let changedAnimation = false;
        let nextAnimation;
        if (animationName !== null) {
            const animation = this.dataMap.animationMap.get(animationName);
            if (animation !== undefined) {
                nextAnimation = animation;
            }
        }
        changedAnimation = nextAnimation !== this.currentAnimation;
        this.currentAnimation = nextAnimation;
        if (changedAnimation) {
            if (this.currentAnimation) {
                if (this.speed >= 0) {
                    this.currentFrame = this.currentAnimation.indexStart;
                }
                else {
                    this.currentFrame = this.currentAnimation.indexEnd;
                }
                const frame = this.dataMap.frameMap.get(this.currentFrame);
                this.currentFrameDurationRemaining = frame?.duration ?? 100;
            }
        }
        return changedAnimation;
    }
    gotoFrame(frameNumber) {
        this.currentFrame = frameNumber;
        const frame = this.dataMap.frameMap.get(this.currentFrame);
        this.currentFrameDurationRemaining = frame?.duration ?? 100;
    }
    gotoAnimationFrame(frameNumber) {
        if (this.currentAnimation) {
            this.currentFrame =
                this.currentAnimation.indexStart +
                    (frameNumber %
                        (this.currentAnimation.indexEnd +
                            1 -
                            this.currentAnimation.indexStart));
        }
        const frame = this.dataMap.frameMap.get(this.currentFrame);
        this.currentFrameDurationRemaining = frame?.duration ?? 100;
    }
    advance(duration) {
        if (this.speed === 0)
            return false;
        this.currentFrameDurationRemaining -= duration * Math.abs(this.speed);
        const initialFrame = this.currentFrame;
        let changedFrame = false;
        let looped = false;
        while (this.currentFrameDurationRemaining <= 0) {
            changedFrame = true;
            if (this.speed > 0) {
                this.currentFrame++;
                if (this.currentAnimation) {
                    if (this.currentFrame > this.currentAnimation.indexEnd) {
                        this.currentFrame = this.currentAnimation.indexStart;
                        looped = true;
                    }
                }
                else {
                    if (this.currentFrame >= this.dataMap.frameMap.size) {
                        this.currentFrame = 0;
                        looped = true;
                    }
                }
            }
            else {
                this.currentFrame--;
                if (this.currentAnimation) {
                    if (this.currentFrame < this.currentAnimation.indexStart) {
                        this.currentFrame = this.currentAnimation.indexEnd;
                        looped = true;
                    }
                }
                else {
                    if (this.currentFrame <= 0) {
                        this.currentFrame = this.dataMap.frameMap.size - 1;
                        looped = true;
                    }
                }
            }
            const frame = this.dataMap.frameMap.get(this.currentFrame) ?? new FrameData();
            this.currentFrameDurationRemaining += frame.duration;
        }
        if (looped && !this.loop && this.currentAnimation !== undefined) {
            if (this.speed > 0) {
                this.currentFrame = this.currentAnimation.indexEnd;
            }
            else {
                this.currentFrame = this.currentAnimation.indexStart;
            }
        }
        if (changedFrame) {
            this.events.emit("FrameSwapped", {
                from: initialFrame,
                to: this.currentFrame
            });
        }
        if (looped) {
            this.events.emit("LoopComplete");
        }
        return changedFrame;
    }
}

export { ProtoSpriteDataMap, ProtoSpriteInstanceAnimationState, createTypedEventEmitter };
//# sourceMappingURL=util.js.map
